import React from 'react';
import './VirtualKeyboard.css';

function VirtualKeyboard({ language, nextKey, currentInput, targetChar, onKeyPress }) {
  const koreanLayout = [
    ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'],
    ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'],
    ['⇧', 'ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ', '<', '>', '?', '⇧'],
    ['스페이스']
  ];

  // 쌍자음 매핑 (Shift + 기본자음)
  const doubleConsonantMap = {
    'ㅃ': 'ㅂ',
    'ㅉ': 'ㅈ', 
    'ㄸ': 'ㄷ',
    'ㄲ': 'ㄱ',
    'ㅆ': 'ㅅ'
  };

  // 왼손/오른손 키 매핑
  const leftHandKeys = {
    // 한글 왼손 키들
    'ㅂ': true, 'ㅈ': true, 'ㄷ': true, 'ㄱ': true, 'ㅅ': true,
    'ㅁ': true, 'ㄴ': true, 'ㅇ': true, 'ㄹ': true, 'ㅎ': true,
    'ㅋ': true, 'ㅌ': true, 'ㅊ': true, 'ㅍ': true,
    // 영어 왼손 키들  
    'q': true, 'w': true, 'e': true, 'r': true, 't': true,
    'a': true, 's': true, 'd': true, 'f': true, 'g': true,
    'z': true, 'x': true, 'c': true, 'v': true,
    // 쌍자음
    'ㅃ': true, 'ㅉ': true, 'ㄸ': true, 'ㄲ': true, 'ㅆ': true
  };

  const rightHandKeys = {
    // 한글 오른손 키들
    'ㅛ': true, 'ㅕ': true, 'ㅑ': true, 'ㅐ': true, 'ㅔ': true,
    'ㅗ': true, 'ㅓ': true, 'ㅏ': true, 'ㅣ': true,
    'ㅠ': true, 'ㅜ': true, 'ㅡ': true,
    // 영어 오른손 키들
    'y': true, 'u': true, 'i': true, 'o': true, 'p': true,
    'h': true, 'j': true, 'k': true, 'l': true,
    'b': true, 'n': true, 'm': true
  };

  // 다음 키 정보 확인 (한글 vs 영어)
  let needsShift = false;
  let baseKey = nextKey;

  if (language === 'ko') {
    // 한글: 쌍자음인지 확인
    needsShift = nextKey && doubleConsonantMap[nextKey];
    baseKey = needsShift ? doubleConsonantMap[nextKey] : nextKey;
  } else {
    // 영어: nextKey가 객체 형태인지 확인
    if (nextKey && typeof nextKey === 'object') {
      needsShift = nextKey.shift;
      baseKey = nextKey.key;
    }
  }

  const englishLayout = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['⇧', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '<', '>', '?', '⇧'],
    ['space']
  ];

  const layout = language === 'ko' ? koreanLayout : englishLayout;

  // 현재 키가 어느 손인지 판단
  const getCurrentHand = () => {
    if (!baseKey) return null;
    
    if (leftHandKeys[baseKey]) return 'left';
    if (rightHandKeys[baseKey]) return 'right';
    return null;
  };

  const currentHand = getCurrentHand();

  const getKeyClass = (key) => {
    const classes = ['keyboard-key'];

    // 스페이스바 스타일
    if (key === '스페이스' || key === 'space') {
      classes.push('key-space');
    }

    // Shift 키 스타일 (이모지 포함)
    if (key === 'Shift' || key === '⇧') {
      classes.push('key-shift');
      // 쌍자음 입력이 필요한 경우 Shift 하이라이트
      if (needsShift) {
        classes.push('key-highlight');
      }
    }

    // 다음 입력해야 할 키 하이라이트 (최우선)
    if (baseKey && key === baseKey) {
      classes.push('key-highlight');
    }
    // 현재 입력된 키들 표시 (하이라이트가 없을 때만)
    else if (currentInput && currentInput.includes(key)) {
      classes.push('key-completed');
    }

    return classes.join(' ');
  };

  const getKeyLabel = (key) => {
    if (key === '스페이스' || key === 'space') {
      return '스페이스';
    }
    if (key === 'Shift' || key === '⇧') {
      return (
        <span className="shift-key-content">
          <span className="shift-icon">⇧</span>
          <span className="shift-text">Shift</span>
        </span>
      );
    }
    return key;
  };

  const handleKeyClick = (key) => {
    if (!onKeyPress) return;
    
    // 실제 키보드 이벤트를 시뮬레이션
    let eventKey = key;
    
    // 특수 키 처리
    if (key === '스페이스' || key === 'space') {
      eventKey = ' ';
    } else if (key === '⇧' || key === 'Shift') {
      // Shift 키는 시뮬레이션하지 않음 (조합키이므로)
      return;
    }
    
    // 키보드 이벤트 생성
    const keyEvent = new KeyboardEvent('keydown', {
      key: eventKey,
      code: `Key${eventKey.toUpperCase()}`,
      bubbles: true
    });
    
    // TypingScreen의 handleKeyPress 호출
    window.dispatchEvent(keyEvent);
  };

  return (
      <div className={`virtual-keyboard ${currentHand ? `hand-hint-${currentHand}` : ''}`}>
        <div className="keyboard-hint">
          {(nextKey || baseKey) && (
              <p className="hint-text">
                {needsShift ? (
                  <>
                    다음 키: <span className="shift-hint">⇧</span> + <span className="next-key-hint">{baseKey}</span>
                    {language === 'ko' && nextKey && <> = <span className="double-consonant-hint">{nextKey}</span></>}
                  </>
                ) : (
                  <>
                    다음 키: <span className="next-key-hint">{baseKey || nextKey}</span>
                  </>
                )}
              </p>
          )}
        </div>

        <div className="keyboard-container">
          {/* 왼손 가이드 이모지 */}
          <div className={`hand-guide left-guide ${currentHand === 'left' ? 'active' : ''}`}>
            <span className="guide-emoji">👈</span>
            <span className="guide-text">왼손</span>
          </div>

          <div className="keyboard-layout">
            {layout.map((row, rowIndex) => (
                <div key={rowIndex} className="keyboard-row">
                  {row.map((key, keyIndex) => (
                      <button
                          key={`${rowIndex}-${keyIndex}`}
                          className={getKeyClass(key)}
                          onClick={() => handleKeyClick(key)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            handleKeyClick(key);
                          }}
                          aria-label={key}
                      >
                        {getKeyLabel(key)}
                      </button>
                  ))}
                </div>
            ))}
          </div>

          {/* 오른손 가이드 이모지 */}
          <div className={`hand-guide right-guide ${currentHand === 'right' ? 'active' : ''}`}>
            <span className="guide-emoji">👉</span>
            <span className="guide-text">오른손</span>
          </div>
        </div>
      </div>
  );
}

export default VirtualKeyboard;